import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Activity, BatteryCharging, Brain, CheckCircle2, Dumbbell, Flame, Quote, Zap } from 'lucide-react';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function Home() {
  return <div className="w-full bg-dark" data-id="element-198">
      {/* Hero Section */}
      <section className="relative bg-dark text-white overflow-hidden border-b border-darkCard" data-id="element-199">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-darkSlate via-dark to-dark opacity-80" data-id="element-200"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20 lg:py-32 flex flex-col lg:flex-row items-center" data-id="element-201">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="w-full lg:w-3/5 pr-0 lg:pr-12" data-id="element-202">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold uppercase tracking-tight leading-[1.1] mb-6" data-id="element-203">
              <span className="text-accent block mb-2 text-3xl md:text-4xl" data-id="element-204">
                [TAGLINE]
              </span>
              Testosterone Optimization for Men in [CITY]
            </h1>
            <p className="text-lg md:text-xl text-slate-400 mb-10 max-w-2xl leading-relaxed" data-id="element-205">
              Convenient, discreet telehealth TRT to help you restore energy,
              focus, and performance. Stop settling for "normal for your age"
              and get back to your peak.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6" data-id="element-206">
              <button className="bg-accent hover:bg-accentHover text-white font-bold py-4 px-8 rounded-md transition-all duration-300 shadow-[0_0_20px_rgba(255,107,53,0.3)] hover:shadow-[0_0_30px_rgba(255,107,53,0.5)] hover:-translate-y-1 uppercase tracking-wide text-lg w-full sm:w-auto" data-id="element-207">
                Schedule your tele-health appointment
              </button>
              <a href="#how-it-works" className="text-slate-400 hover:text-accent font-semibold underline underline-offset-4 transition-colors uppercase tracking-wider text-sm" data-id="element-208">
                Learn how it works
              </a>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="w-full lg:w-2/5 mt-16 lg:mt-0 hidden md:block" data-id="element-209">
            {/* Abstract Geometric Composition replacing the image */}
            <div className="relative w-full h-[500px] rounded-lg overflow-hidden border-2 border-darkCard shadow-2xl bg-darkSlate flex items-center justify-center group" data-id="element-210">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-accent/10 via-transparent to-transparent" data-id="element-211"></div>

              {/* Abstract shapes */}
              <div className="absolute top-10 right-10 w-64 h-64 bg-accent/10 rounded-full blur-3xl group-hover:bg-accent/20 transition-all duration-700" data-id="element-212"></div>
              <div className="absolute bottom-10 left-10 w-48 h-48 bg-accent/5 rounded-full blur-2xl" data-id="element-213"></div>

              {/* Geometric core */}
              <div className="relative z-10 w-3/4 h-3/4 border border-accent/30 transform rotate-3 flex items-center justify-center transition-transform duration-700 group-hover:rotate-6" data-id="element-214">
                <div className="w-full h-full border-2 border-accent transform -rotate-6 bg-dark/80 backdrop-blur-sm flex items-center justify-center transition-transform duration-700 group-hover:-rotate-12 shadow-[0_0_30px_rgba(255,107,53,0.1)]" data-id="element-215">
                  <div className="text-center" data-id="element-216">
                    <Activity className="w-24 h-24 text-accent mx-auto mb-6 opacity-90" data-id="element-217" />
                    <div className="inline-block bg-accent text-white text-sm font-bold uppercase tracking-widest py-2 px-4 rounded-sm shadow-lg" data-id="element-218">
                      Performance Driven
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-darkSlate border-b border-darkCard" data-id="element-219">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-220">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-221">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white uppercase tracking-tight mb-6" data-id="element-222">
              How It Works
            </h2>
            <div className="w-24 h-1.5 bg-accent mx-auto" data-id="element-223"></div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-224">
            {[{
            step: '01',
            title: 'Online evaluation with a licensed provider',
            desc: 'Complete a straightforward telehealth consultation from the privacy of your home.'
          }, {
            step: '02',
            title: 'Labs and personalized TRT plan',
            desc: 'Get comprehensive bloodwork done locally, followed by a custom protocol designed for your physiology.'
          }, {
            step: '03',
            title: 'Ongoing optimization via telehealth',
            desc: 'Medication delivered to your door with regular check-ins to dial in your optimal levels.'
          }].map((item, i) => <motion.div variants={fadeInUp} key={i} className="relative p-8 bg-dark border border-darkCard rounded-md shadow-lg group hover:border-accent/50 transition-colors duration-300" data-id="element-225">
                <div className="text-7xl font-heading font-bold text-darkCard absolute top-4 right-6 pointer-events-none group-hover:text-accent/10 transition-colors duration-300" data-id="element-226">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-227">
                  <h3 className="text-2xl font-heading font-bold text-white mb-4 pr-12 uppercase tracking-wide" data-id="element-228">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed" data-id="element-229">{item.desc}</p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-dark border-b border-darkCard" data-id="element-230">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-231">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-232">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white uppercase tracking-tight mb-6" data-id="element-233">
              Reclaim Your Edge
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-234">
              Optimize your hormones to unlock the physical and mental
              performance you've been missing.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-235">
            {[{
            icon: <Zap className="w-8 h-8 text-accent" data-id="element-236" />,
            title: 'More Energy',
            desc: 'Wake up rested and power through your day without the afternoon crash.'
          }, {
            icon: <Brain className="w-8 h-8 text-accent" data-id="element-237" />,
            title: 'Improved Focus',
            desc: 'Clear the brain fog and regain sharp, sustained mental clarity.'
          }, {
            icon: <Dumbbell className="w-8 h-8 text-accent" data-id="element-238" />,
            title: 'Stronger Performance',
            desc: 'Better workouts, faster recovery, and easier lean muscle retention.'
          }, {
            icon: <Flame className="w-8 h-8 text-accent" data-id="element-239" />,
            title: 'Improved Drive',
            desc: 'Restore your libido and sexual performance to optimal levels.'
          }, {
            icon: <Activity className="w-8 h-8 text-accent" data-id="element-240" />,
            title: 'Better Mood',
            desc: 'Reduce irritability and regain your natural confidence and drive.'
          }, {
            icon: <BatteryCharging className="w-8 h-8 text-accent" data-id="element-241" />,
            title: 'Better Sleep',
            desc: 'Experience deeper, more restorative sleep cycles every night.'
          }].map((benefit, i) => <motion.div variants={fadeInUp} key={i} className="flex items-start p-6 bg-darkSlate rounded-md shadow-sm border border-darkCard" data-id="element-242">
                <div className="flex-shrink-0 mr-5 bg-accent/10 p-3 rounded-md border border-accent/20" data-id="element-243">
                  {benefit.icon}
                </div>
                <div data-id="element-244">
                  <h3 className="text-xl font-heading font-bold text-white mb-2 uppercase tracking-wide" data-id="element-245">
                    {benefit.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed" data-id="element-246">
                    {benefit.desc}
                  </p>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Why Clinic Section */}
      <section className="py-24 bg-darkSlate border-b border-darkCard" data-id="element-247">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-248">
          <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-249">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="w-full lg:w-1/2" data-id="element-250">
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-white uppercase tracking-tight mb-8" data-id="element-251">
                Why <span className="text-accent" data-id="element-252">[CLINIC NAME]</span>?
              </h2>
              <p className="text-lg text-slate-400 mb-6 leading-relaxed" data-id="element-253">
                We don't believe in cookie-cutter medicine. Our licensed
                providers use evidence-based protocols to deliver personalized
                TRT directly to you via telehealth. No waiting rooms, no
                stigma—just results.
              </p>
              <p className="text-lg text-slate-400 leading-relaxed" data-id="element-254">
                We focus on clinical excellence and patient convenience,
                ensuring you get the highest standard of care without disrupting
                your busy schedule.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="w-full lg:w-1/2 bg-dark rounded-md p-8 md:p-10 border border-darkCard shadow-2xl relative overflow-hidden" data-id="element-255">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-bl-full" data-id="element-256"></div>
              <h3 className="text-2xl font-heading font-bold text-white uppercase tracking-wider mb-8 border-b border-darkCard pb-4 relative z-10" data-id="element-257">
                The [CLINIC NAME] Standard
              </h3>
              <ul className="space-y-6 relative z-10" data-id="element-258">
                {["Licensed medical providers specializing in men's health", 'Evidence-based, personalized treatment protocols', '100% secure and discreet telehealth platform', 'Direct-to-door medication delivery', 'Ongoing lab monitoring and dosage optimization', 'No hidden fees or confusing pricing tiers'].map((point, i) => <li key={i} className="flex items-start" data-id="element-259">
                    <CheckCircle2 className="w-6 h-6 text-accent mr-4 flex-shrink-0 mt-0.5" data-id="element-260" />
                    <span className="text-slate-300 font-medium" data-id="element-261">{point}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Results & Social Proof */}
      <section className="py-24 bg-dark" data-id="element-262">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-263">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-264">
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-white uppercase tracking-tight mb-6" data-id="element-265">
              Real Results
            </h2>
            <div className="w-24 h-1.5 bg-accent mx-auto" data-id="element-266"></div>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20" data-id="element-267">
            {[{
            quote: 'Within 6 weeks, the brain fog lifted and my energy in the gym was back to where it was in my 20s. The telehealth process was incredibly easy.',
            initial: 'M.T.',
            age: '42'
          }, {
            quote: 'I thought feeling tired all the time was just part of getting older. [CLINIC NAME] got my labs sorted and the personalized protocol changed everything.',
            initial: 'J.R.',
            age: '38'
          }, {
            quote: 'No waiting rooms, no awkward conversations. Just straight-forward, professional medical care that actually delivered on its promises.',
            initial: 'D.S.',
            age: '45'
          }].map((testimonial, i) => <motion.div variants={fadeInUp} key={i} className="bg-darkSlate p-8 rounded-md shadow-lg border border-darkCard relative" data-id="element-268">
                <Quote className="w-12 h-12 text-darkCard absolute top-6 left-6" data-id="element-269" />
                <div className="relative z-10" data-id="element-270">
                  <p className="text-slate-300 italic mb-8 leading-relaxed text-lg" data-id="element-271">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center" data-id="element-272">
                    <div className="w-12 h-12 bg-dark border border-accent/30 rounded-full flex items-center justify-center text-accent font-heading font-bold mr-4" data-id="element-273">
                      {testimonial.initial}
                    </div>
                    <div data-id="element-274">
                      <p className="font-heading font-bold text-white tracking-wide" data-id="element-275">
                        {testimonial.initial}
                      </p>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mt-1" data-id="element-276">
                        Patient, Age {testimonial.age}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </motion.div>

          {/* Optional Stats Row */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center border-t border-darkCard pt-16" data-id="element-277">
            <div data-id="element-278">
              <div className="text-5xl font-heading font-bold text-accent mb-3" data-id="element-279">
                98%
              </div>
              <div className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-280">
                Patient Satisfaction
              </div>
            </div>
            <div data-id="element-281">
              <div className="text-5xl font-heading font-bold text-accent mb-3" data-id="element-282">
                24/7
              </div>
              <div className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-283">
                Message Access
              </div>
            </div>
            <div data-id="element-284">
              <div className="text-5xl font-heading font-bold text-accent mb-3" data-id="element-285">
                50+
              </div>
              <div className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-286">
                States Licensed
              </div>
            </div>
            <div data-id="element-287">
              <div className="text-5xl font-heading font-bold text-accent mb-3" data-id="element-288">
                100%
              </div>
              <div className="text-sm font-bold text-slate-400 uppercase tracking-wider" data-id="element-289">
                Confidential
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Strip */}
      <section className="bg-accent py-20 relative overflow-hidden" data-id="element-290">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white/10 to-transparent opacity-50" data-id="element-291"></div>
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeInUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-292">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-white uppercase tracking-tight mb-10 drop-shadow-md" data-id="element-293">
            Ready to feel like yourself again?
          </h2>
          <button className="bg-dark hover:bg-darkSlate text-white font-bold py-5 px-10 rounded-md transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 text-lg w-full sm:w-auto uppercase tracking-wide border border-darkCard" data-id="element-294">
            Schedule your tele-health appointment
          </button>
        </motion.div>
      </section>
    </div>;
}